import type { ReactElement } from 'react';
import { useMemo, useState, useCallback } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type { PersonalRunningNowListItem } from '@kirin/api-client/contracts/account-shadow-ai-running-now';
import type { RunningNowSortField } from '@kirin/api-client/contracts/shadow-ai-running-now';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { RUNNING_PROCESSES_REFRESH_INTERVAL } from '@/hooks/api/useShadowAiTools';
import {
  ExpandedProcessRow,
  SHADOW_AI_TABLE_SCROLL_CLASS,
  RUNNING_NOW_NOTE,
  RUNNING_NOW_APPROVAL_TOOLTIPS,
} from '@/components/shared/shadow-ai';
import { AgentStatus } from '../components';
import { Skeleton } from '@/components/ui/skeleton';
import { queryKeys } from '@/hooks/api/queryKeys';
import {
  TOOL_APPROVAL_SCOPES,
  TOOL_APPROVAL_TABS,
  type ToolApprovalFilter,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { usePersonalToolApprovalTable } from '@/components/shared/shadow-ai/usePersonalToolApprovalTable';
import { createToolApprovalColumn } from '@/components/shared/shadow-ai/toolApprovalColumn';
import { createPersonalRunningNowColumns } from './columns';
import { fetchPersonalRunningNow, type PersonalRunningNowMeta } from './api';
import { PERSONAL_RUNNING_NOW_TABLE_CONFIG } from './constants';

const TABLE_ID = PERSONAL_RUNNING_NOW_TABLE_CONFIG.tableId;

function buildQueryKeys(
  params: DataFetchParams,
  rootOnly: boolean,
  approval?: ToolApprovalFilter
) {
  // Under the `runningNow` root so an approve / unapprove (which invalidates
  // it) refreshes this table too.
  const base = [
    ...queryKeys.runningNow.all,
    TABLE_ID,
    'dataTable',
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as RunningNowSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
      approval,
    },
  ] as const;
  return rootOnly ? [...base, { isRoot: true }] : base;
}

interface DataTablePersonalRunningNowContentProps {
  lastHeartbeat: string | null;
  isLoading: boolean;
  onMeta: (meta: PersonalRunningNowMeta) => void;
}

export function DataTablePersonalRunningNowContent({
  lastHeartbeat,
  isLoading,
  onMeta,
}: DataTablePersonalRunningNowContentProps): ReactElement {
  const [rootOnly, setRootOnly] = useState(false);

  // KIR-4864: the owner approves on their personal account + Show filter.
  const approvalTable = usePersonalToolApprovalTable({
    tableId: TABLE_ID,
    tab: TOOL_APPROVAL_TABS.RUNNING,
    countsRefetchInterval: RUNNING_PROCESSES_REFRESH_INTERVAL,
  });
  const { approvalFilter } = approvalTable;
  const showApproval = approvalTable.enabled;

  const columns = useMemo(() => {
    const base = createPersonalRunningNowColumns();
    if (!showApproval) return base;
    return [
      ...base,
      createToolApprovalColumn<PersonalRunningNowListItem>({
        getToolId: row => row.toolId,
        scope: TOOL_APPROVAL_SCOPES.ENDPOINT,
        personal: true,
        actionTooltips: RUNNING_NOW_APPROVAL_TOOLTIPS,
      }),
    ];
  }, [showApproval]);

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchPersonalRunningNow(
        params,
        signal,
        rootOnly ? true : undefined,
        approvalFilter
      ),
    [rootOnly, approvalFilter]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) =>
      buildQueryKeys(params, rootOnly, approvalFilter),
    [rootOnly, approvalFilter]
  );

  const renderExpandedContent = useCallback(
    (row: PersonalRunningNowListItem) => <ExpandedProcessRow row={row} />,
    []
  );

  const searchConfig = useMemo(
    () => ({
      placeholder: 'Search running processes...',
      className: 'w-[260px]',
    }),
    []
  );

  const featuresConfig = useMemo(
    () => ({ enableSorting: true, enableRowExpansion: true }),
    []
  );

  const rowExpansionConfig = useMemo(
    () => ({
      getRowId: (row: PersonalRunningNowListItem) => row.id,
      render: renderExpandedContent,
      contentClassName: 'bg-muted/30 p-4',
      rowClassName: 'bg-muted/10',
      keepMounted: false as const,
      showContainer: false as const,
      highlightOnExpand: true,
      scrollToExpandedRow: true,
    }),
    [renderExpandedContent]
  );

  const queryConfig = useMemo(
    () => ({
      staleTime: 15000,
      refetchOnWindowFocus: true,
      refetchInterval: RUNNING_PROCESSES_REFRESH_INTERVAL,
    }),
    []
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-base">Running Now</CardTitle>
            <AgentStatus lastHeartbeat={lastHeartbeat} isLoading={isLoading} />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="rootOnly"
              checked={rootOnly}
              onCheckedChange={setRootOnly}
            />
            <label
              htmlFor="rootOnly"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Root processes only
            </label>
          </div>
        </div>
        <CardDescription>{RUNNING_NOW_NOTE}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {approvalTable.isFlagLoading ? (
          <div data-testid="tool-approval-flag-loading">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <DataTable<PersonalRunningNowListItem, PersonalRunningNowMeta>
            key={rootOnly ? 'root-only' : 'all'}
            id={TABLE_ID}
            mode="server"
            className="flex flex-col"
            containerClassName={SHADOW_AI_TABLE_SCROLL_CLASS}
            columns={columns}
            errorBoundaryLabel="Something went wrong in the Running Now table"
            search={searchConfig}
            features={featuresConfig}
            rowExpansion={rowExpansionConfig}
            queryFn={queryFn}
            getQueryKeysFromParams={queryKeyFn}
            onMeta={onMeta}
            noDataLabel="No running processes found."
            queryConfig={queryConfig}
            {...approvalTable.dataTableProps}
          />
        )}
      </CardContent>
    </Card>
  );
}

DataTablePersonalRunningNowContent.displayName =
  'DataTablePersonalRunningNowContent';
